import React from 'react';
import { getIn } from 'formik';
import { Button, Col, Form, Row } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import DatePickerField from './DatePickerField';
import { MAX_PO_INVOICES, newPoInvoice } from '../utils/poSales';
import './QuoteProductsTable.css';

// Invoices belong to the PO as a whole and are managed independently of its products.
export default function PoInvoicesTable({ formik }) {
  const rows = formik.values.invoices || [];
  const disabled = formik.isSubmitting;
  const setRows = next => formik.setFieldValue('invoices', next);
  const change = (index, field, value) =>
    setRows(rows.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row));
  const remove = index => {
    if (rows.length <= 1) return; // at least one invoice row always stays
    // Keep validation and touched state attached to the remaining rows.
    const touched = formik.touched.invoices;
    const errors = formik.errors.invoices;
    if (Array.isArray(touched)) formik.setTouched({ ...formik.touched, invoices: touched.filter((_, i) => i !== index) }, false);
    if (Array.isArray(errors)) formik.setErrors({ ...formik.errors, invoices: errors.filter((_, i) => i !== index) });
    setRows(rows.filter((_, i) => i !== index));
  };
  const addAfter = index => {
    if (rows.length >= MAX_PO_INVOICES) return;
    const next = [...rows];
    next.splice(index + 1, 0, newPoInvoice());
    setRows(next);
  };
  const globalError = typeof formik.errors.invoices === 'string' && formik.submitCount > 0 ? formik.errors.invoices : '';

  return (
    <section className="quote-products" aria-label="Invoices">
      <div className="quote-products__heading">
        <h5>Invoices</h5>
        <span>{rows.length} {rows.length === 1 ? 'invoice' : 'invoices'}</span>
      </div>
      <div className="po-invoices__box">
      {rows.map((row, index) => {
        const field = name => 'invoices[' + index + '].' + name;
        const error = name => {
          const message = getIn(formik.errors, field(name));
          return message && (getIn(formik.touched, field(name)) || formik.submitCount > 0) ? message : '';
        };
                const dateId = 'po-invoice-' + index + '-invoice_date';
        const label = (text, required = true) => (
          <div className="quote-products__field-label d-block mb-2" aria-hidden="true">{text}{required && <span className="text-danger">*</span>}</div>
        );
        const feedback = name => error(name) && <div className="invalid-feedback d-block" role="alert">{error(name)}</div>;
        const textInput = (name, ariaLabel, maxLength, placeholder) => (
          <Form.Control name={field(name)} aria-label={ariaLabel + ' row ' + (index + 1)} maxLength={maxLength}
            value={row[name] ?? ''} placeholder={placeholder} disabled={disabled}
            onChange={event => change(index, name, event.target.value)}
            onBlur={formik.handleBlur} isInvalid={Boolean(error(name))} />
        );
        return (
          <Row key={row._key || row.id || index} className="align-items-start g-3 po-invoices__row">
            <Col md={3}>
              {label('Invoice No:')}
              {textInput('invoice_no', 'Invoice Number', 100, 'Invoice No.')}
              {feedback('invoice_no')}
            </Col>
            <Col md={2}>
              {label('Date')}
              <DatePickerField id={dateId} name={field('invoice_date')} value={row.invoice_date ?? ''}
                ariaLabel={'Invoice Date row ' + (index + 1)}
                onChange={value => change(index, 'invoice_date', value)}
                onBlur={() => formik.setFieldTouched(field('invoice_date'), true)}
                disabled={disabled} isInvalid={Boolean(error('invoice_date'))} />
              {feedback('invoice_date')}
            </Col>
            <Col md={3}>
              {label('Transporter Name')}
              {textInput('transporter_name', 'Invoice Transporter Name', 150)}
              {feedback('transporter_name')}
            </Col>
            <Col md={3}>
              {label('Reference Number')}
              {textInput('reference_number', 'Invoice Reference Number', 100)}
              {feedback('reference_number')}
            </Col>
            <Col md={1} className="d-flex justify-content-end gap-2 pt-md-4">
              <Button type="button" variant="outline-primary" size="sm" aria-label={'Add invoice row ' + (index + 1)}
                title="Add invoice row" disabled={disabled || rows.length >= MAX_PO_INVOICES} onClick={() => addAfter(index)}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
              <Button type="button" variant="outline-danger" size="sm" aria-label={'Delete invoice row ' + (index + 1)}
                title={rows.length <= 1 ? 'At least one invoice row is required' : 'Delete invoice row'}
                disabled={disabled || rows.length <= 1} onClick={() => remove(index)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </Button>
            </Col>
          </Row>
        );
      })}
      {!rows.length && (
        <div className="quote-products__empty py-2">
          No invoices.
          <Button type="button" variant="outline-primary" size="sm" className="ms-2" aria-label="Add invoice row 1"
            title="Add invoice row" disabled={disabled} onClick={() => setRows([newPoInvoice()])}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>
      )}
      </div>
      {globalError && <div className="invalid-feedback d-block" role="alert">{globalError}</div>}
    </section>
  );
}
